import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import type {
  AccountInfo,
  ApiKeyRow,
  SandboxCreatedResponse,
} from '../api/client';
import { ApiError, useAuth } from '../auth/AuthProvider';
import { MaskedSecret } from '../components/MaskedSecret';
import { formatDate } from '../lib/format';

export function SettingsPage() {
  const { fetcher } = useAuth();
  const qc = useQueryClient();

  const accountQ = useQuery({
    queryKey: ['account'],
    queryFn: () => fetcher<AccountInfo>('/api/account'),
  });
  const keysQ = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => fetcher<{ keys: ApiKeyRow[] }>('/api/account/api-keys'),
  });

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Settings</h1>
      {/* G1 — cards seed their inputs from server state and re-sync on change
          via dirty-tracking (useServerSyncedState), so a background refetch
          neither leaves inputs stale nor discards in-progress edits. */}
      {accountQ.data ? (
        <BrandingCard account={accountQ.data} qc={qc} fetcher={fetcher} />
      ) : null}
      {accountQ.data ? (
        <WebhookCard account={accountQ.data} qc={qc} fetcher={fetcher} />
      ) : null}
      {accountQ.data ? (
        <CustomDomainCard account={accountQ.data} qc={qc} fetcher={fetcher} />
      ) : null}
      {keysQ.data ? (
        <ApiKeysCard rows={keysQ.data.keys} qc={qc} fetcher={fetcher} />
      ) : null}
      {accountQ.data ? (
        <SandboxCard account={accountQ.data} fetcher={fetcher} />
      ) : null}
    </div>
  );
}

type Fetcher = ReturnType<typeof useAuth>['fetcher'];
type QC = ReturnType<typeof useQueryClient>;

/**
 * G1 — seed an input from server state, and RE-seed it when the server value
 * changes ONLY if the user hasn't edited since the last seed (dirty-tracking).
 * This replaces the previous remount-by-key approach, which discarded
 * in-progress edits whenever a background `['account']` refetch resolved.
 */
function useServerSyncedState(
  serverValue: string,
): [string, (v: string) => void] {
  const [value, setValue] = useState(serverValue);
  const seededRef = useRef(serverValue);
  useEffect(() => {
    setValue((cur) => (cur === seededRef.current ? serverValue : cur));
    seededRef.current = serverValue;
  }, [serverValue]);
  return [value, setValue];
}

function BrandingCard({
  account,
  qc,
  fetcher,
}: {
  account: AccountInfo;
  qc: QC;
  fetcher: Fetcher;
}) {
  const b = (account.branding ?? {}) as Record<string, string>;
  const [companyName, setCompanyName] = useServerSyncedState(b['company_name'] ?? '');
  const [primaryColor, setPrimaryColor] = useServerSyncedState(
    b['primary_color'] ?? '#2E7D4F',
  );
  const [replyEmail, setReplyEmail] = useServerSyncedState(b['reply_email'] ?? '');
  const [completionUrl, setCompletionUrl] = useServerSyncedState(
    b['completion_url'] ?? '',
  );
  const [logoUrl, setLogoUrl] = useServerSyncedState(b['logo_url'] ?? '');

  const saveM = useMutation({
    mutationFn: () =>
      fetcher<AccountInfo>('/api/account', {
        method: 'PATCH',
        body: JSON.stringify({
          branding: {
            company_name: companyName,
            primary_color: primaryColor,
            reply_email: replyEmail,
            completion_url: completionUrl,
            logo_url: logoUrl,
          },
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['account'] }),
  });

  return (
    <div className="card space-y-2">
      <h2 className="text-sm font-medium uppercase text-slate-500">Branding</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className="label">Company name</label>
          <input
            className="input mt-1"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Primary color</label>
          <input
            className="input mt-1"
            type="color"
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Reply email</label>
          <input
            className="input mt-1"
            value={replyEmail}
            onChange={(e) => setReplyEmail(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Completion URL</label>
          <input
            className="input mt-1"
            value={completionUrl}
            onChange={(e) => setCompletionUrl(e.target.value)}
          />
        </div>
        <div className="md:col-span-2">
          <label className="label">Logo URL</label>
          <input
            className="input mt-1"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
          />
          {/* TODO(phase-11): POST /api/account/logo (multipart) — wire when
              the backend file-upload route exists. */}
        </div>
      </div>
      <button
        type="button"
        className="btn-primary"
        disabled={saveM.isPending}
        onClick={() => saveM.mutate()}
      >
        Save branding
      </button>
      {saveM.isError ? (
        <p className="text-sm text-red-600">
          {(saveM.error as Error).message}
        </p>
      ) : null}
    </div>
  );
}

function WebhookCard({
  account,
  qc,
  fetcher,
}: {
  account: AccountInfo;
  qc: QC;
  fetcher: Fetcher;
}) {
  const [url, setUrl] = useServerSyncedState(account.webhook_url ?? '');
  const [secret, setSecret] = useState('');

  // G5 — test-fire reuses /api/contracts/:id/events, so it needs a contract
  // to attach to. Gate the button on the contract count instead of letting
  // it surface the raw "no contracts to attach test event to" error.
  const contractsQ = useQuery({
    queryKey: ['contracts', 'count'],
    queryFn: () => fetcher<{ contracts: { id: string }[] }>('/api/contracts'),
  });
  const hasContracts = (contractsQ.data?.contracts.length ?? 0) > 0;

  const saveM = useMutation({
    mutationFn: () =>
      fetcher<AccountInfo>('/api/account', {
        method: 'PATCH',
        body: JSON.stringify({
          webhook_url: url || null,
          ...(secret ? { webhook_secret: secret } : {}),
        }),
      }),
    onSuccess: () => {
      setSecret('');
      qc.invalidateQueries({ queryKey: ['account'] });
    },
  });

  // Test-fire: POST a delivery.test event via /events to round-trip the
  // dispatcher locally. Note: requires a contract id; we use the most
  // recent contract if any. If there is none, button is disabled.
  // TODO(phase-11): expose a dedicated test-fire endpoint that does not
  // require a contract reference.
  const testFireM = useMutation({
    mutationFn: async () => {
      const list = await fetcher<{ contracts: { id: string }[] }>(
        '/api/contracts',
      );
      const first = list.contracts[0];
      if (!first) throw new Error('no contracts to attach test event to');
      return fetcher(`/api/contracts/${first.id}/events`, {
        method: 'POST',
        body: JSON.stringify({
          type: 'delivery.test',
          payload: { source: 'client-ui-test-fire' },
        }),
      });
    },
  });

  return (
    <div className="card space-y-2">
      <h2 className="text-sm font-medium uppercase text-slate-500">Webhook</h2>
      <div className="space-y-2">
        <div>
          <label className="label">URL</label>
          <input
            className="input mt-1"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Secret (write-only)</label>
          <input
            className="input mt-1"
            type="password"
            placeholder="enter a new secret to rotate"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-primary"
            disabled={saveM.isPending}
            onClick={() => saveM.mutate()}
          >
            Save
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => testFireM.mutate()}
            disabled={testFireM.isPending || !hasContracts}
            title={
              hasContracts
                ? undefined
                : 'Create a contract first — test-fire attaches to an existing contract.'
            }
          >
            Test fire
          </button>
        </div>
        {!hasContracts ? (
          <p className="text-sm text-slate-500">
            Create a contract first to test-fire a webhook delivery.
          </p>
        ) : testFireM.isError ? (
          <p className="text-sm text-red-600">
            {(testFireM.error as Error).message}
          </p>
        ) : testFireM.isSuccess ? (
          <p className="text-sm text-emerald-700">delivery.test queued.</p>
        ) : null}
      </div>
    </div>
  );
}

function CustomDomainCard({
  account,
  qc,
  fetcher,
}: {
  account: AccountInfo;
  qc: QC;
  fetcher: Fetcher;
}) {
  const [domain, setDomain] = useServerSyncedState(account.custom_domain ?? '');
  const saveM = useMutation({
    mutationFn: () =>
      fetcher<AccountInfo>('/api/account', {
        method: 'PATCH',
        body: JSON.stringify({ custom_domain: domain || null }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['account'] }),
  });
  const recheckM = useMutation({
    mutationFn: () =>
      fetcher('/api/account/custom-domain/recheck', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['account'] }),
  });
  return (
    <div className="card space-y-2">
      <h2 className="text-sm font-medium uppercase text-slate-500">
        Custom domain
      </h2>
      <div className="flex gap-2">
        <input
          className="input"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="sign.acme.com"
        />
        <button
          type="button"
          className="btn-primary"
          disabled={saveM.isPending}
          onClick={() => saveM.mutate()}
        >
          Save
        </button>
        <button
          type="button"
          className="btn"
          disabled={!account.custom_domain || recheckM.isPending}
          onClick={() => recheckM.mutate()}
        >
          Re-check
        </button>
      </div>
      <p className="text-sm text-slate-600">
        Status: <span className="font-medium">{account.custom_domain_status}</span>
        {account.custom_domain_checked_at ? (
          <span className="text-slate-400">
            {' '}
            (checked {formatDate(account.custom_domain_checked_at)})
          </span>
        ) : null}
      </p>
      {saveM.isError ? (
        <p className="text-sm text-red-600">
          {saveM.error instanceof ApiError
            ? `${saveM.error.code ?? saveM.error.status}: ${saveM.error.message}`
            : (saveM.error as Error).message}
        </p>
      ) : null}
    </div>
  );
}

function ApiKeysCard({
  rows,
  qc,
  fetcher,
}: {
  rows: ApiKeyRow[];
  qc: QC;
  fetcher: Fetcher;
}) {
  const [name, setName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const createM = useMutation({
    mutationFn: () =>
      fetcher<{ key: string }>('/api/account/api-keys', {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),
    onSuccess: (resp) => {
      setCreatedKey(resp.key);
      setName('');
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });
  const revokeM = useMutation({
    mutationFn: (id: string) =>
      fetcher(`/api/account/api-keys/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });
  return (
    <div className="card space-y-2">
      <h2 className="text-sm font-medium uppercase text-slate-500">API keys</h2>
      <div className="flex gap-2">
        <input
          className="input"
          placeholder="key name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button
          type="button"
          className="btn-primary"
          disabled={!name.trim() || createM.isPending}
          onClick={() => createM.mutate()}
        >
          Create
        </button>
      </div>
      {createdKey ? (
        <div className="rounded bg-emerald-50 p-2 text-sm">
          <p className="mb-1 font-medium">
            New API key — copy now, it will not be shown again.
          </p>
          <MaskedSecret value={createdKey} />
        </div>
      ) : null}
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-slate-500">
          <tr>
            <th className="px-2 py-1">Name</th>
            <th className="px-2 py-1">Prefix</th>
            <th className="px-2 py-1">Created</th>
            <th className="px-2 py-1">Revoked</th>
            <th className="px-2 py-1"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100">
              <td className="px-2 py-1">{r.name}</td>
              <td className="px-2 py-1 font-mono">
                {r.prefix}…{r.last4}
              </td>
              <td className="px-2 py-1 text-slate-600">
                {formatDate(r.created_at)}
              </td>
              <td className="px-2 py-1 text-slate-600">
                {formatDate(r.revoked_at)}
              </td>
              <td className="px-2 py-1 text-right">
                {!r.revoked_at ? (
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={() => revokeM.mutate(r.id)}
                  >
                    Revoke
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SandboxCard({
  account,
  fetcher,
}: {
  account: AccountInfo;
  fetcher: Fetcher;
}) {
  const [result, setResult] = useState<SandboxCreatedResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const provisionM = useMutation({
    mutationFn: () =>
      fetcher<SandboxCreatedResponse>('/api/account/sandbox', {
        method: 'POST',
      }),
    onSuccess: (r) => {
      setResult(r);
      setErr(null);
    },
    onError: (e: unknown) => {
      setErr(e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message);
    },
  });
  useEffect(() => {
    setResult(null);
    setErr(null);
  }, [account.id]);
  if (account.is_sandbox) {
    return (
      <div className="card text-sm text-slate-600">
        This is a sandbox account. Sandboxes cannot create child sandboxes.
      </div>
    );
  }
  return (
    <div className="card space-y-2">
      <h2 className="text-sm font-medium uppercase text-slate-500">Sandbox</h2>
      <button
        type="button"
        className="btn"
        disabled={provisionM.isPending}
        onClick={() => provisionM.mutate()}
      >
        Provision sandbox account
      </button>
      {err ? <p className="text-sm text-red-600">{err}</p> : null}
      {result ? (
        <div className="rounded bg-emerald-50 p-2 text-sm">
          <p>
            Sandbox slug: <code>{result.sandbox_account.slug}</code>
          </p>
          <p className="mb-1 font-medium">
            Bootstrap API key (copy now — shown once):
          </p>
          <MaskedSecret value={result.api_key.key} />
        </div>
      ) : null}
    </div>
  );
}
